import React from "react";
import { Container, Typography, Box, Button } from "@mui/material";
import { motion } from "framer-motion";
import { trackEvent } from "../utils/analytics";
import { colors } from "../theme/tokens";

const steps = [
  {
    number: "1",
    title: "Sign up in seconds",
    description:
      "Create your account and dive right in. No complex setup or configuration needed.",
  },
  {
    number: "2",
    title: "Add your first project",
    description:
      "Create a project, add some tasks, and start organizing your work your way.",
  },
  {
    number: "3",
    title: "Stay focused and productive",
    description:
      "Track time, take notes, and complete tasks in a clutter-free environment.",
  },
];

const HowItWorks: React.FC = () => {
  const handleGetStartedClick = () => {
    trackEvent("redirect_to_app", {
      location: "how_it_works_section",
      destination: "app",
    });
  };

  return (
    <Box
      id="how-it-works"
      sx={{
        background: colors.surface,
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: "800px", px: 0 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            component={motion.h2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            sx={{
              fontSize: { xs: "30px", md: "40px" },
              fontWeight: 800,
              letterSpacing: "-0.01em",
              color: colors.ink,
              m: 0,
              mb: 2,
            }}
          >
            Get started in{" "}
            <Box component="span" sx={{ color: colors.primary }}>
              3 simple steps
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: "17px",
              color: colors.muted,
              maxWidth: "520px",
              mx: "auto",
            }}
          >
            No tutorials, no overwhelming setup screens. Just sign up and start
            being productive.
          </Typography>
        </Box>

        <Box>
          {steps.map((step, index) => (
            <Box
              key={step.number}
              component={motion.div}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              sx={{
                display: "flex",
                gap: 3.5,
                alignItems: "flex-start",
                mb: 5,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  border: `2px solid ${colors.primary}`,
                  color: colors.primary,
                  fontSize: "22px",
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {step.number}
              </Box>
              <Box>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: colors.ink,
                    mt: "6px",
                    mb: 1,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{ color: colors.muted, lineHeight: 1.6, fontSize: "16px" }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            textAlign: "center",
            mt: 6,
            p: { xs: 4, md: 6 },
            borderRadius: "20px",
            background: colors.ink,
          }}
        >
          <Typography
            component="h3"
            sx={{
              color: "#fff",
              fontSize: { xs: "22px", md: "26px" },
              fontWeight: 700,
              m: 0,
              mb: 1.5,
            }}
          >
            Ready to get started?
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.65)",
              mb: 3.5,
              fontSize: "16px",
            }}
          >
            Join thousands who've simplified their workflow with Renoten.
          </Typography>
          <Button
            disableElevation
            href="https://app.renoten.com/signup"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleGetStartedClick}
            sx={{
              background: colors.primary,
              color: "#fff",
              fontWeight: 600,
              fontSize: "16px",
              textTransform: "none",
              px: 3.5,
              py: 1.75,
              borderRadius: "10px",
              transition: "all 0.3s ease",
              "&:hover": {
                background: colors.primaryHover,
                transform: "translateY(-2px)",
              },
            }}
          >
            Start Free Today →
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks;
